import React, { useState, ChangeEvent, FC, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Backdrop, Fade, Typography, IconButton, FormControl } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { MOBILE_PRINTER_NUMBER, PRINTER_NUMBER_MAX_LENGTH } from '../../../constants';

import styles from './MobilePrinterModal.css';
import Button from '../../buttons/Button';
import { mergeClassNames } from '../../../utils';
import PrinterNumberField from '../../inputs/PrinterNumberField';
import RadioButton from '../../buttons/RadioButton';

type Props = {
  isOpenDialog: boolean;
  onModalClose: () => void;
};

const MobilePrinterModal: FC<Props> = ({ isOpenDialog, onModalClose }) => {
  const [printerChoice, setPrinterChoice] = useState<'yes' | 'no' | undefined>();
  const [printerNumber, setPrinterNumber] = useState<string>('');
  const [printerNumberError, setPrinterNumberError] = useState<boolean>(false);

  const { t } = useTranslation();
  const translationRoot = 'MOBILE_PRINTER';

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'yes' | 'no';
    setPrinterChoice(value);
    if (value === 'no') {
      setPrinterNumber('');
      setPrinterNumberError(false);
    }
  };

  const handlePrinterInput = (e: ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, PRINTER_NUMBER_MAX_LENGTH);
    setPrinterNumber(digitsOnly);
    if (digitsOnly && printerNumberError) {
      setPrinterNumberError(false);
    }
  };

  const resetFields = useCallback(() => {
    setPrinterChoice(undefined);
    setPrinterNumberError(false);
    setPrinterNumber('');
    onModalClose();
  }, [onModalClose]);

  const handleSubmit = () => {
    if (printerChoice === 'yes' && printerNumber.trim() === '') {
      setPrinterNumberError(true);
      return;
    }

    localStorage.setItem(MOBILE_PRINTER_NUMBER, printerNumber);
    resetFields();
  };

  const handleCancel = () => {
    localStorage.setItem(MOBILE_PRINTER_NUMBER, '');
    resetFields();
  };

  useEffect(() => {
    if (!isOpenDialog) {
      setPrinterChoice(undefined);
      setPrinterNumber('');
    }
  }, [isOpenDialog]);

  return (
    <Modal
      open={isOpenDialog}
      onClose={onModalClose}
      className={styles.modalRoot}
      closeAfterTransition
      disableBackdropClick
      disableEscapeKeyDown
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
      aria-labelledby="mobile-printer-title"
      aria-describedby="mobile-printer-description"
    >
      <Fade in={isOpenDialog}>
        <div className={styles.modalPaper} role="dialog" aria-modal="true">
          <div className={styles.modalHeader}>
            <h2 className={styles.modalHeading}>{t(`${translationRoot}.MOBILE_PRINTER_HEADING`)}</h2>
            <IconButton onClick={handleCancel} className={styles.closeButton} aria-label="Close modal">
              <CloseIcon />
            </IconButton>
          </div>

          <div className={styles.modalContent} id="mobile-printer-description">
            <FormControl component="fieldset" className={styles.radioGroup}>
              <div role="radiogroup" aria-label="Printer usage selection">
                <RadioButton
                  id="printer-no"
                  name="printer-choice"
                  value="no"
                  label={t(`${translationRoot}.DEFAULT_PRINTER`)}
                  checked={printerChoice === 'no'}
                  onChange={handleRadioChange}
                  className={styles.customRadio}
                />
                <RadioButton
                  id="printer-yes"
                  name="printer-choice"
                  value="yes"
                  label={t('YES')}
                  checked={printerChoice === 'yes'}
                  onChange={handleRadioChange}
                  className={styles.customRadio}
                />
              </div>
            </FormControl>

            {printerChoice === 'yes' && (
              <div className={styles.inputSection}>
                <Typography
                  className={styles.inputLabel}
                  id="printer-number-label"
                  htmlFor="printer-number"
                  component="label"
                >
                  {t(`${translationRoot}.MOBILE_PRINTER_NUMBER`)}
                </Typography>
                <PrinterNumberField
                  id="filled-basic"
                  label={t(`${translationRoot}.PRINTER`)}
                  value={printerNumber}
                  onChange={handlePrinterInput}
                  error={printerNumberError}
                  maxLength={PRINTER_NUMBER_MAX_LENGTH}
                />
                {printerNumberError && (
                  <div id="printer-number-error" className={styles.errorText}>
                    {t(`${translationRoot}.MOBILE_PRINTER_NUMBER`)}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={styles.modalFooter}>
            <Button
              className={mergeClassNames(styles.btn, styles.cancelButton)}
              data-cy="cancel-button"
              aria-label={t(`${translationRoot}.CANCEL`)}
              onClick={handleCancel}
            >
              {t(`${translationRoot}.CANCEL`)}
            </Button>
            <Button
              className={mergeClassNames(styles.btn, styles.submitButton)}
              data-cy="submit-button"
              disabled={!printerChoice}
              aria-label={t(`${translationRoot}.SUBMIT`)}
              onClick={handleSubmit}
            >
              {t(`${translationRoot}.SUBMIT`)}
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default MobilePrinterModal;
